import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/game-config';
import { NEON_FONTS } from '@/config/colors';

const FONT_TITLE = NEON_FONTS.TITLE;
const TITLE_DISPLAY_MS = 1200;
const COUNTDOWN_STEP_MS = 1000;

interface LevelData {
  readonly score: number;
  readonly lives: number;
  readonly level: number;
  readonly extraLifeAwarded: boolean;
  readonly collectedFruits?: string[];
}

export class LevelTransitionScene extends Phaser.Scene {
  private levelData: LevelData | null = null;
  private countdownText: Phaser.GameObjects.Text | null = null;
  private titleText: Phaser.GameObjects.Text | null = null;
  private hasStartedNextLevel = false;

  constructor() {
    super({ key: 'LevelTransitionScene' });
  }

  create(data: LevelData): void {
    this.levelData = data;
    this.hasStartedNextLevel = false;

    const centerX = GAME_WIDTH / 2;
    const centerY = GAME_HEIGHT / 2;

    // Dark navy background
    this.cameras.main.setBackgroundColor('#050520');

    // Show level title
    this.titleText = this.add.text(centerX, centerY - 50, `LEVEL ${data.level}`, {
      fontFamily: FONT_TITLE,
      fontSize: '28px',
      fontStyle: 'bold',
      color: '#FFE500',
      shadow: { offsetX: 0, offsetY: 0, color: '#FFE500', blur: 16, fill: true },
    }).setOrigin(0.5);

    // Add scale-in animation for title
    this.titleText.setScale(0);
    this.tweens.add({
      targets: this.titleText,
      scaleX: 1,
      scaleY: 1,
      duration: 500,
      ease: 'Back.easeOut',
    });

    // Create countdown text (hidden initially)
    this.countdownText = this.add.text(centerX, centerY + 40, '', {
      fontFamily: FONT_TITLE,
      fontSize: '40px',
      fontStyle: 'bold',
      color: '#FFFFFF',
    }).setOrigin(0.5).setAlpha(0);

    // Start countdown sequence after title displays
    this.time.delayedCall(TITLE_DISPLAY_MS, () => {
      this.animateCountdownNumber(3, '#FF4444');
    });

    this.time.delayedCall(TITLE_DISPLAY_MS + COUNTDOWN_STEP_MS, () => {
      this.animateCountdownNumber(2, '#FFAA00');
    });

    this.time.delayedCall(TITLE_DISPLAY_MS + COUNTDOWN_STEP_MS * 2, () => {
      this.animateCountdownNumber(1, '#44FF44');
    });

    // Start gameplay immediately after countdown finishes
    this.time.delayedCall(TITLE_DISPLAY_MS + COUNTDOWN_STEP_MS * 3, () => {
      this.startNextLevel();
    });
  }

  private animateCountdownNumber(num: number, color: string): void {
    if (!this.countdownText) {
      return;
    }

    // Reset for new number
    this.countdownText.setText(String(num));
    this.countdownText.setColor(color);
    this.countdownText.setAlpha(1);
    this.countdownText.setScale(2.5);

    // Scale down and settle animation
    this.tweens.add({
      targets: this.countdownText,
      scaleX: 1,
      scaleY: 1,
      duration: 400,
      ease: 'Back.easeOut',
    });

    // Fade out near end of step
    this.tweens.add({
      targets: this.countdownText,
      alpha: 0.2,
      duration: 300,
      delay: 650,
      ease: 'Cubic.easeIn',
    });
  }

  private startNextLevel(): void {
    if (!this.levelData || this.hasStartedNextLevel) {
      return;
    }
    this.hasStartedNextLevel = true;

    // Clean up
    if (this.titleText) {
      this.titleText.destroy();
      this.titleText = null;
    }
    if (this.countdownText) {
      this.countdownText.destroy();
      this.countdownText = null;
    }

    // Start GameScene with preserved state
    this.scene.start('GameScene', {
      score: this.levelData.score,
      lives: this.levelData.lives,
      level: this.levelData.level,
      extraLifeAwarded: this.levelData.extraLifeAwarded,
      collectedFruits: this.levelData.collectedFruits ?? [],
    });

    this.levelData = null;
  }
}
